import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useToast } from '../../components/common/Toast';
import { getArabicDate } from '../../services/utils/date';
import teacherService from '../../services/api/teacher';
import adminService from '../../services/api/admin';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatDuration } from '../../utils/videoUtils';
import DirectVideoUpload from '../../components/teacher/DirectVideoUpload';
import { 
  Image as ImageIcon, 
  Upload, 
  X, 
  Save, 
  ArrowRight, 
  Layout, 
  FileText, 
  Eye, 
  ChevronRight,
  AlertCircle,
  Settings,
  Calendar,
  Layers,
  Sparkles,
  CheckCircle2,
  Trash2,
  Plus,
  Clock,
  Gift,
  CreditCard,
  Lock,
  Unlock,
  XCircle
} from 'lucide-react';

// Dynamic description function for settings
const getSettingDescription = (setting, value) => {
  const descriptions = {
    isFree: {
      true: 'متاح للجميع بدون اشتراك',
      false: 'يتطلب اشتراك فعال'
    },
    isLockedForNonSubscribers: {
      true: 'مقفول على غير المشتركين فقط',
      false: 'متاح للجميع'
    },
    isActive: {
      true: 'متاح للطلاب حالياً',
      false: 'غير متاح مؤقتاً'
    },
    isPublished: {
      true: 'منشور للطلاب',
      false: 'مسودة - غير منشور'
    }
  };
  
  return descriptions[setting]?.[value] || '';
};

// Enhanced setting titles with icons
const getSettingTitle = (setting, value) => {
  const titles = {
    isFree: {
      true: 'درس مجاني',
      false: 'درس مدفوع'
    },
    isLockedForNonSubscribers: {
      true: 'مقفول للمشتركين',
      false: 'متاح للجميع'
    },
    isActive: {
      true: 'درس نشط',
      false: 'درس معطل'
    },
    isPublished: {
      true: 'درس منشور',
      false: 'درس مسودة'
    }
  };
  
  return titles[setting]?.[value] || '';
};

const EditLesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check for admin mode
  const isAdminMode = useMemo(() => searchParams.get('mode') === 'admin', [searchParams]);
  
  // Helper functions for video URLs
  const getVideoEmbedUrl = (videoUrl) => {
    if (!videoUrl) return '';
    
    // YouTube
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
    
    // Vimeo
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    
    // Dailymotion
    const dailymotionMatch = videoUrl.match(/dailymotion\.com\/video\/([a-zA-Z0-9]+)/);
    if (dailymotionMatch) return `https://www.dailymotion.com/embed/video/${dailymotionMatch[1]}?autoplay=1`;
    
    return videoUrl;
  };
  
  // Debug Logs removed for production
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    courseId: '',
    contentType: 'video',
    videoType: 'external', // Default to external
    isFree: false,
    freeUntilDate: '',
    isLockedForNonSubscribers: true,
    thumbnailUrl: '',
    isActive: true,
    order: 0,
    durationSeconds: null,
    linkedQuizId: ''
  });

  // File states for video upload
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setErrors] = useState({});
  
  // استقبال بيانات الدرس من صفحة دروسي إذا وجدت
  useEffect(() => {
    // لا نستخدم location.state بعد الآن - نعتمد فقط على API
    // location.state ignored - using only API data
  }, [location.state]);
  
  // جلب بيانات الدرس الحالية
  const { data: lesson, isLoading, error } = useQuery(
    ['lesson', lessonId, isAdminMode],
    () => {
      return isAdminMode ? adminService.getLesson(lessonId) : teacherService.getLesson(lessonId);
    },
    {
      enabled: !!lessonId && lessonId !== 'undefined',
      onSuccess: (data) => {
        
        // نستخدم دائماً بيانات API
        setFormData({
          title: data.title || '',
          description: data.description || '',
          videoUrl: data.video_url || '',
          videoType: data.video_type || 'external',
          courseId: data.course_id || '',
          contentType: data.content_type || 'video',
          isFree: data.is_free || false,
          freeUntilDate: data.free_until_date ? new Date(data.free_until_date).toISOString().split('T')[0] : '',
          isLockedForNonSubscribers: data.is_locked_for_non_subscribers !== undefined ? data.is_locked_for_non_subscribers : true,
          thumbnailUrl: data.thumbnail_url || '',
          isActive: data.is_active !== undefined ? data.is_active : true,
          order: data.order || 0,
          durationSeconds: data.duration_seconds || null,
          linkedQuizId: data.linked_quiz_id || ''
        });
        
        // ✅ Fetch secure thumbnail URL using the dedicated endpoint
        if (data.thumbnail_url) {
          teacherService.getLessonImageUrl(lessonId)
            .then(imageResponse => {
              if (imageResponse?.data?.imageUrl) {
                setThumbnailPreview(imageResponse.data.imageUrl);
              }
            })
            .catch(error => {
              // Fallback if fetching secure URL fails
            });
        }
      },
      onError: (error) => {
        toast.error('فشل في تحميل بيانات الدرس');
        navigate(isAdminMode ? '/admin/content' : '/teacher/lessons');
      }
    }
  );
  
  // جلب الكورسات الخاصة بالمدرس أو جميع الكورسات للأدمن
  const { data: subjects } = useQuery(
    ['teacher-courses', isAdminMode],
    () => isAdminMode ? adminService.getCourses() : teacherService.getTeacherCourses(),
    { 
      select: (data) => {
        // Extract courses array from response
        const courses = isAdminMode ? (data?.courses || []) : (data?.courses || data?.data?.courses || []);
        return courses;
      }
    }
  );

  // جلب الاختبارات الخاصة بالمدرس
  const { data: quizzes = [], isLoading: quizzesLoading } = useQuery(
    'teacher-quizzes',
    () => teacherService.getTeacherQuizzes(),
    { 
      retry: 1,
      enabled: !isAdminMode, // فقط للمعلمين وليس الأدمن
      select: (data) => {
        return data?.quizzes || [];
      }
    }
  );
  
  // تحديث الدرس (Mutation)
  const updateLessonMutation = useMutation(
    (updatedData) => isAdminMode ? adminService.updateContent('lessons', lessonId, updatedData) : teacherService.updateLesson(lessonId, updatedData),
    {
      onSuccess: (response) => {
        toast.success('تم تحديث الدرس بنجاح!');
        queryClient.invalidateQueries(isAdminMode ? ['admin-content', 'lessons'] : 'teacher-lessons');
        queryClient.invalidateQueries(['lesson', lessonId]);
        navigate(isAdminMode ? '/admin/content' : '/teacher/lessons');
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
          const errorSummary = error.response.data.errors
            .map(err => `${err.param}: ${err.msg}`)
            .join(' | ');
          toast.error(`أخطاء: ${errorSummary}`);
        } else {
          const errMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'فشل في تحديث الدرس، حاول مرة أخرى.';
          setErrors({ submit: errMsg });
          toast.error(errMsg);
        }
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    }
  );
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('حجم الصورة كبير جداً (الأقصى 50 ميجابايت)');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('يجب اختيار ملف صورة صالح');
        return;
      }

      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const removeThumbnail = () => {
    setThumbnail(null);
    setThumbnailPreview('');
    setFormData(prev => ({ ...prev, thumbnailUrl: '' }));
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      // Validate file size (100MB max for PDFs/documents)
      if (uploadedFile.size > 100 * 1024 * 1024) {
        toast.error('حجم الملف كبير جداً (الأقصى 100 ميجابايت)');
        return;
      }
      
      // Allowed file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (!allowedTypes.includes(uploadedFile.type)) {
        toast.error('نوع الملف غير مدعوم. يرجى استخدام PDF, Word, PowerPoint, Excel, أو Text');
        return;
      }

      setFile(uploadedFile);
      
      // Create file preview info
      const fileSizeInMB = (uploadedFile.size / (1024 * 1024)).toFixed(2);
      const fileExtension = uploadedFile.name.split('.').pop().toUpperCase();
      
      setFilePreview({
        name: uploadedFile.name,
        size: fileSizeInMB,
        type: fileExtension
      });
    }
  };

  const removeFile = () => {
    setFile(null);
    setFilePreview(null);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('يجب إدخال عنوان الدرس');
      return;
    }

    if (!formData.courseId) {
      toast.error('يجب اختيار الكورس الدراسي');
      return;
    }

    setIsSubmitting(true);
    
    // Prepare data for submission
    let lessonData;
    
    // Always use FormData for video upload support (النمط الاحترافي الموحد)
    lessonData = new FormData();
    lessonData.append('courseId', String(formData.courseId));
    lessonData.append('title', formData.title.trim());
    lessonData.append('description', formData.description || '');
    lessonData.append('contentType', formData.contentType);
    
    // booleans - explicit string conversion
    lessonData.append('isFree', String(formData.isFree));
    lessonData.append('isLockedForNonSubscribers', String(formData.isLockedForNonSubscribers));
    
    // numbers - append only if not null/undefined/empty (القاعدة الذهبية)
    if (formData.order != null && formData.order !== '')
      lessonData.append('order', String(formData.order));
    
    if (formData.durationSeconds != null && formData.durationSeconds !== '')
      lessonData.append('durationSeconds', String(formData.durationSeconds));
    
    // Add video based on type - single handler for all contentType cases
    if (formData.contentType === 'video') {
      // 🔍 CRITICAL: Normalize videoType - handle array, JSON string, or regular string
      let normalizedVideoType = 'external'; // Default
      
      if (formData.videoType) {
        // If it's a string that looks like JSON or "{"upload","upload"}" format
        if (typeof formData.videoType === 'string') {
          // Try to parse as JSON array first
          try {
            if (formData.videoType.startsWith('{') || formData.videoType.startsWith('[')) {
              const parsed = JSON.parse(formData.videoType);
              normalizedVideoType = Array.isArray(parsed) ? parsed[0] : parsed;
            } else {
              // It's a regular string
              normalizedVideoType = formData.videoType;
            }
          } catch (e) {
            // If JSON parsing fails, try to extract first occurrence
            if (formData.videoType.includes('upload')) {
              normalizedVideoType = 'upload';
            } else if (formData.videoType.includes('external')) {
              normalizedVideoType = 'external';
            } else {
              normalizedVideoType = formData.videoType;
            }
          }
        } else if (Array.isArray(formData.videoType)) {
          // If it's already an array, take first element
          normalizedVideoType = formData.videoType[0] || 'external';
        }
      }
      
      // Ensure it's one of the valid values
      if (!['upload', 'external'].includes(normalizedVideoType)) {
        normalizedVideoType = 'external';
      }
      
      lessonData.append('videoType', normalizedVideoType);
      
      if (normalizedVideoType === 'external' && formData.videoUrl) {
        lessonData.append('videoUrl', formData.videoUrl);
      }
      // Note: Direct upload is handled via DirectVideoUpload component
    } else if (formData.contentType === 'file' && file) {
      // رفع ملف (PDF/Word/Excel/etc)
      lessonData.append('file', file);
    }
    
    // Add thumbnail if exists
    if (thumbnail) {
      lessonData.append('thumbnail', thumbnail);
    }

    // Add linkedQuizId if exists
    if (formData.linkedQuizId) {
      lessonData.append('linkedQuizId', formData.linkedQuizId);
    }
    
    // Only add freeUntilDate if it has a value
    if (formData.freeUntilDate) {
      lessonData.append('freeUntilDate', formData.freeUntilDate);
    }
    
    // Add isActive status
    lessonData.append('isActive', String(formData.isActive));
    
    // 🔍 DIAGNOSTIC LOGGING - تشخيص بيانات الطلب
    try {
      const entries = Array.from(lessonData.entries());
      const fieldNames = entries.map(([key]) => key);
      const duplicates = fieldNames.filter((item, index) => fieldNames.indexOf(item) !== index);
      
      if (duplicates.length > 0) {
        console.warn(`⚠️  DUPLICATE FIELDS DETECTED: ${duplicates.join(', ')}`);
      }
    } catch (logError) {
      // Silent catch for logging errors
    }

    updateLessonMutation.mutate(lessonData);
  };
  
  // معالجة حالات التحميل والخطأ
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل بيانات الدرس...</p>
      </div>
    );
  }

  if (error || !lessonId || lessonId === 'undefined') {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-red-100">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">خطأ في العثور على الدرس</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            {error?.message || 'لم يتم العثور على الدرس المطلوب أو أن الرابط غير صالح.'}
          </p>
          <button
            onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/lessons')}
            className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-bold hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto shadow-lg"
          >
            <ArrowRight size={18} /> العودة {isAdminMode ? 'لإدارة المحتوى' : 'للدروس'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-10 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <button
                onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/lessons')}
                className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
            >
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                العودة {isAdminMode ? 'لإدارة المحتوى' : 'لإدارة الدروس'}
            </button>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <div className="bg-blue-600 text-white p-1.5 rounded-xl"><Sparkles size={24} /></div>
                تعديل بيانات الدرس
            </h1>
        </div>
        <div className="flex items-center gap-3">
            <div className="bg-white px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-xs font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                <Calendar size={14} className="text-primary-500" />
                تعديل أخير: {getArabicDate(lesson.updated_at || lesson.created_at)}
            </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10">
            <h2 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
              <Layout size={20} className="text-primary-600" />
              معلومات الدرس الأساسية
            </h2>

            <div className="space-y-6">
              {/* Title Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> عنوان الدرس الحالي *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الدرس الجديد"
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  required
                />
              </div>

              {/* Course Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Layers size={16} className="text-primary-500" /> الكورس الدراسي *
                </label>
                <div className="relative">
                    <select
                    id="courseId"
                    name="courseId"
                    value={formData.courseId}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                    required
                    >
                    <option value="">اختر الكورس المناسب</option>
                    {subjects?.map((course) => (
                        <option key={course.id} value={course.id}>
                        {course.title}
                        </option>
                    ))}
                    </select>
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <ChevronRight className="rotate-90" size={20} />
                    </div>
                </div>
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> وصف الدرس وتفاصيله
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="حدث وصف الدرس لطلابك..."
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed"
                />
                <p className="text-[10px] text-gray-400 font-bold px-2 text-left">{formData.description.length} حرف تم استخدامه</p>
              </div>

              {/* Content Type Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> نوع المحتوى
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, contentType: 'video' }))} 
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                      formData.contentType === 'video'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Eye size={20} className="mx-auto mb-2" />
                    فيديو
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, contentType: 'file', videoUrl: '', videoType: 'external' }))} 
                    className={`p-4 rounded-2xl border-2 transition-all font-bold text-sm ${
                      formData.contentType === 'file'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Upload size={20} className="mx-auto mb-2" />
                    ملف
                  </button>
                </div>
              </div>

              {/* Video Type Selection - Only show for video content */}
              {formData.contentType === 'video' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Settings size={16} className="text-primary-500" /> نوع الفيديو
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, videoType: 'external', videoUrl: '' }))}
                    className={`p-4 rounded-2xl border-2 transition-all font-bold ${
                      formData.videoType === 'external'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Upload className="w-5 h-5 mx-auto mb-2" />
                    رابط خارجي
                    <p className="text-xs opacity-75">YouTube, Vimeo</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, videoType: 'upload', videoUrl: '' }))}
                    className={`p-4 rounded-2xl border-2 transition-all font-bold ${
                      formData.videoType === 'upload'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="w-5 h-5 mx-auto mb-2" />
                    رفع مباشر
                    <p className="text-xs opacity-75">MP4, AVI, MOV</p>
                  </button>
                </div>
              </div>
              )}

              {/* File Upload - Only show for file content */}
              {formData.contentType === 'file' && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <Upload size={16} className="text-primary-500" /> رفع الملف (PDF, Word, Excel, إلخ)
                  </label>
                  
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary-400 transition-colors">
                    {filePreview ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center gap-4 bg-gray-50 rounded-xl p-6">
                          <div className="bg-primary-100 text-primary-600 p-4 rounded-lg">
                            <FileText size={32} />
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-gray-900">{filePreview.name}</p>
                            <p className="text-sm text-gray-500">{filePreview.size} MB • {filePreview.type}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="bg-red-600 text-white px-6 py-3 rounded-2xl hover:bg-red-700 transition-all font-bold flex items-center gap-2 mx-auto"
                        >
                          <Trash2 size={20} />
                          حذف الملف
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="w-16 h-16 text-gray-400 mx-auto" />
                        <div>
                          <p className="text-lg font-bold text-gray-700">اختر ملفاً لرفعه</p>
                          <p className="text-sm text-gray-500">PDF, Word, Excel, PowerPoint, Text (حتى 100MB)</p>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                          onChange={handleFileChange}
                          className="hidden"
                          id="file-upload-edit"
                        />
                        <label
                          htmlFor="file-upload-edit"
                          className="bg-primary-600 text-white px-8 py-4 rounded-2xl hover:bg-primary-700 transition-all font-bold cursor-pointer inline-flex items-center gap-2"
                        >
                          <Plus size={20} />
                          اختر ملف
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Video Input Based on Type - Only show for video content */}
              {formData.contentType === 'video' && formData.videoType === 'external' ? (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <Eye size={16} className="text-primary-500" /> رابط الفيديو الخارجي
                  </label>
                  <input
                    type="text"
                    id="videoUrl"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleInputChange}
                    placeholder="https://youtube.com/watch?v=... أو https://vimeo.com/..."
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                  />
                  {formData.videoUrl && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 font-medium mb-2">معاينة الفيديو:</p>
                      <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-lg">
                        <iframe
                          src={getVideoEmbedUrl(formData.videoUrl)}
                          className="w-full h-full"
                          title="Video Preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )}

                  {/* Duration Input - Required for all external videos */}
                  <div className="mt-4 space-y-2">
                    <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                        <Clock size={16} className="text-primary-500" /> مدة الفيديو (بالثواني)
                        <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="durationSeconds"
                      name="durationSeconds"
                      value={formData.durationSeconds || ''}
                      onChange={handleInputChange}
                      placeholder="مثال: 300 (لـ 5 دقائق)"
                      min="1"
                      max="14400" // 4 ساعات كحد أقصى
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                      required
                    />
                    <p className="text-xs text-gray-500 font-medium">
                        💡 أدخل مدة الفيديو بالثواني (مثال: 600 = 10 دقائق)
                    </p>
                    {formData.durationSeconds && (
                      <p className="text-xs text-green-600 font-medium">
                        ⏱️ المدة: {formatDuration(formData.durationSeconds)}
                      </p>
                    )}
                  </div>
                </div>
              ) : formData.contentType === 'video' ? (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                      <Upload size={16} className="text-primary-500" /> رفع الفيديو
                  </label>
                  <DirectVideoUpload
                    courseId={formData.courseId}
                    initialData={{
                      title: formData.title,
                      description: formData.description,
                      durationSeconds: formData.durationSeconds,
                      order: formData.order,
                      isFree: formData.isFree,
                      freeUntilDate: formData.freeUntilDate,
                      isLockedForNonSubscribers: formData.isLockedForNonSubscribers
                    }}
                    thumbnailFile={thumbnail}
                    onUploadComplete={() => {
                      toast.success('تم رفع الفيديو وتحديث الدرس بنجاح!');
                      queryClient.invalidateQueries('teacher-lessons');
                      navigate(isAdminMode ? '/admin/content' : '/teacher/lessons');
                    }}
                    disabled={!formData.courseId || !formData.title}
                  />
                </div>
              ) : null}

              {/* Free Until Date Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Calendar size={16} className="text-primary-500" /> متاح مجاناً حتى تاريخ (اختياري)
                </label>
                <input
                  type="date"
                  id="freeUntilDate"
                  name="freeUntilDate"
                  value={formData.freeUntilDate}
                  onChange={handleInputChange}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Settings Area */}
        <div className="space-y-6">
          
          {/* Thumbnail Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3">
                <ImageIcon size={18} className="text-primary-600" /> صورة الدرس
            </h2>

            <div className="space-y-6">
              {thumbnailPreview ? (
                <div className="relative group rounded-3xl overflow-hidden shadow-xl ring-4 ring-gray-50 transition-all">
                  <img
                    src={thumbnailPreview}
                    alt="Preview"
                    className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      type="button"
                      onClick={removeThumbnail}
                      className="bg-red-600 text-white p-3 rounded-2xl hover:bg-red-700 transition-all shadow-lg transform hover:scale-110"
                      title="حذف الصورة"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>
              ) : (
                <label 
                  htmlFor="thumbnail"
                  className="w-full aspect-video border-4 border-dashed border-gray-100 rounded-[2rem] flex flex-col items-center justify-center group hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <div className="bg-primary-50 text-primary-600 p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform">
                    <Upload size={32} />
                  </div>
                  <span className="text-sm font-black text-gray-900">اضغط لرفع صورة</span>
                  <span className="text-[10px] font-bold text-gray-400 mt-1">JPG, PNG (Max 50MB)</span>
                </label>
              )}
              
              <input
                type="file"
                id="thumbnail"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="hidden"
              />
              
              {thumbnailPreview && (
                  <label htmlFor="thumbnail" className="w-full py-3 px-4 border border-gray-100 rounded-xl font-bold text-xs text-primary-600 bg-primary-50/50 hover:bg-primary-100 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm">
                      <Upload size={14} /> تغيير صورة الدرس
                  </label>
              )}
            </div>
          </div>

          {/* Settings Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3 uppercase tracking-tighter">
                <Settings size={18} className="text-primary-600" /> إعدادات الدرس
            </h2>

            {/* Visibility Toggle */}
            <div className="bg-gray-50 p-5 rounded-2xl flex items-center justify-between group transition-all hover:bg-gray-100/50">
                <div className="flex flex-col">
                    <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                        {formData.isFree ? <Gift size={14} className="text-green-600" /> : <CreditCard size={14} className="text-blue-600" />}
                        {getSettingTitle('isFree', formData.isFree)}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                        {getSettingDescription('isFree', formData.isFree)}
                    </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="isFree"
                        name="isFree"
                        checked={formData.isFree}
                        onChange={handleInputChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
            </div>

            {/* Locked Toggle */}
            <div className="bg-gray-50 p-5 rounded-2xl flex items-center justify-between group transition-all hover:bg-gray-100/50">
                <div className="flex flex-col">
                    <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                        {formData.isLockedForNonSubscribers ? <Lock size={14} className="text-orange-600" /> : <Unlock size={14} className="text-purple-600" />}
                        {getSettingTitle('isLockedForNonSubscribers', formData.isLockedForNonSubscribers)}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                        {getSettingDescription('isLockedForNonSubscribers', formData.isLockedForNonSubscribers)}
                    </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="isLockedForNonSubscribers"
                        name="isLockedForNonSubscribers"
                        checked={formData.isLockedForNonSubscribers}
                        onChange={handleInputChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
            </div>

            {/* Active Toggle */}
            <div className="bg-gray-50 p-5 rounded-2xl flex items-center justify-between group transition-all hover:bg-gray-100/50">
                <div className="flex flex-col">
                    <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                        {formData.isActive ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                        {getSettingTitle('isActive', formData.isActive)}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                        {getSettingDescription('isActive', formData.isActive)}
                    </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
            </div>

            {/* Order Input */}
            <div className="space-y-2">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mr-2">ترتيب العرض (أولوية)</label>
                <div className="relative">
                    <input
                        type="number"
                        id="order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900"
                    />
                    <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                </div>
            </div>

{/* Quiz Binding Section */}
<div className="mt-10 pt-8 border-t border-gray-200">
  <div className="bg-white rounded-2xl p-7 border border-gray-200">

    {/* Header */}
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-lg font-bold text-gray-900">
          ربط اختبار بهذا الدرس
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          اختياري
        </p>
      </div>

      <button
        type="button"
        onClick={() =>
          window.open(
            `/teacher/quizzes/create?lesson=${
              formData.courseId ? formData.order : ""
            }`,
            "_blank"
          )
        }
        className="bg-yellow-500 text-white px-4 py-2 rounded-lg 
                   hover:bg-yellow-600 transition-colors 
                   text-sm font-medium"
      >
        إنشاء اختبار
      </button>
    </div>

    {/* Description */}
    <p className="text-sm text-gray-600 mb-6 leading-relaxed">
      يمكنك ربط اختبار موجود بهذا الدرس لتفعيل عملية التقييم للطلاب،
      أو إنشاء اختبار جديد وربطه لاحقاً.
    </p>

    {/* Select */}
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-800">
        اختر الاختبار:
      </label>

      <select
        value={formData.linkedQuizId}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            linkedQuizId: e.target.value,
          }))
        }
        className="w-full px-4 py-3 rounded-xl 
                   border border-gray-300 
                   focus:border-yellow-500 
                   focus:ring-2 focus:ring-yellow-100 
                   focus:outline-none 
                   bg-white text-gray-700"
        disabled={quizzesLoading}
      >
        <option value="">-- لا يوجد اختبار مرتبط --</option>

        {quizzesLoading ? (
          <option disabled>جاري جلب الاختبارات...</option>
        ) : quizzes.length === 0 ? (
          <option disabled>
            لا توجد اختبارات متاحة - قم بإنشاء واحد أولاً
          </option>
        ) : (
          quizzes.map((quiz) => (
            <option key={quiz.id} value={quiz.id}>
              {quiz.title}
              {quiz.lesson_title && ` (${quiz.lesson_title})`}
            </option>
          ))
        )}
      </select>
    </div>

    {/* Success message */}
    {formData.linkedQuizId && (
      <div className="mt-5 p-3 bg-green-50 border border-green-200 rounded-lg">
        <p className="text-sm text-green-700">
          تم ربط الاختبار بنجاح.
        </p>
      </div>
    )}

  </div>
            </div>

            {/* Save Buttons */}
            <div className="pt-4 space-y-4">
                <button
                    type="submit"
                    disabled={updateLessonMutation.isLoading || isSubmitting}
                    className="w-full bg-primary-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50 transform active:scale-95"
                >
                    <Save size={20} />
                    {updateLessonMutation.isLoading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/lessons')}
                    className="w-full py-4 rounded-2xl font-bold text-gray-400 hover:bg-gray-50 transition-all text-sm border border-transparent hover:border-gray-100"
                >
                    إلغاء التعديل
                </button>
            </div>
          </div>

          {/* Quick Info Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10 space-y-3">
                <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center">
                    <Eye size={20} />
                </div>
                <h3 className="font-black text-lg">نصيحة التعديل</h3>
                <p className="text-gray-400 text-xs leading-relaxed font-medium">
                    تأكد من مراجعة رابط الفيديو ومدة الدرس دورياً لضمان تجربة تعليمية سلسة للطلاب، مما يزيد من نسبة إكمال الدروس.
                </p>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default EditLesson;
